<script setup>
import { computed, ref, watch } from 'vue'
import StreamService from '@/services/StreamService'

// Settings › Broadcast › Chat. Chat is the Castr player's own audience panel
// (contract D3). The site makes room for it automatically whenever the live
// stream's Audience panel is on (runtime v0.18), so there is nothing to set
// here: turning chat on, moderation and its look live on the stream in Castr.
const props = defineProps({
	/** The content item in the big player when it resolves (for the stream link). */
	liveItem: {
		type: Object,
		default: null,
	},
})

const streamId = computed(() => (props.liveItem?.castr_ref?.type === 'live' ? props.liveItem.castr_ref.id : null))
const streamTitle = computed(() => props.liveItem?.title || 'your stream')

// Same rule as the runtime: the panel is drawn when Audience is on with at least one feature.
const AUDIENCE_FEATURES = ['chat', 'qa', 'polls', 'reactions']
const chatOn = ref(null)
watch(streamId, async (id) => {
	chatOn.value = null
	if (!id) return
	try {
		const audience = (await StreamService.getStreamMetadata(id))?.audience
		if (streamId.value !== id) return
		chatOn.value = !!audience && audience.enabled === true && AUDIENCE_FEATURES.some((key) => audience[key] === true)
	} catch {
		chatOn.value = null
	}
}, { immediate: true })
</script>

<template>
	<section class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="broadcast-chat">
		<h3 class="mb-1 text-sm font-semibold text-white">Chat</h3>
		<p class="mb-3 text-tiny text-surface-7">Viewers can chat beside the video when chat is turned on for your live stream.</p>
		<p v-if="chatOn !== null" class="mb-3 flex items-center gap-2 text-sm text-surface-text-1" data-testid="broadcast-chat-status">
			<span class="size-2 shrink-0 rounded-full" :class="chatOn ? 'bg-success' : 'bg-surface-6'" aria-hidden="true"></span>
			Chat is {{ chatOn ? 'on' : 'off' }} for {{ streamTitle }}
		</p>
		<p class="mb-0 text-tiny text-surface-7" data-testid="broadcast-chat-help">
			Turn it on or off in Castr → Live streams → <template v-if="streamId"><router-link :to="{ name: 'LivestreamManageAudience', params: { streamId } }" class="text-main-light underline-offset-2 hover:underline" data-testid="broadcast-chat-stream-link">{{ streamTitle }}</router-link></template><template v-else>&lt;stream&gt;</template> → Audience (moderation is there too). The site makes room for it automatically.
		</p>
	</section>
</template>
